import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import IconButton from '@mui/material/IconButton';
import Input from '@mui/material/Input';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import { Icons } from 'config/icons';
import React from 'react';
import { UserPlaylist } from 'types';
import App from 'AppInterface/App';
import { SHARE_ORIGIN } from 'config';

interface SharePlaylistProps {
	open: boolean;
	playlist: UserPlaylist;
	onShare: (playlistId: string, phoneNumbers: string[], isPubliclyShared: boolean) => Promise<void>;
	onClose: () => void;
}

export default function SharePlaylist(props: SharePlaylistProps) {
	const { open, playlist, onShare, onClose } = props;
	const [phoneNumber, setPhoneNumber] = React.useState('');
	const [phoneNumbers, setPhoneNumbers] = React.useState<string[]>(playlist?.sharedWith || []);
	const [originalPhoneNumbers] = React.useState<string[]>(playlist?.sharedWith || []);
	const [isPubliclyShared, setIsPubliclyShared] = React.useState<boolean>(playlist?.isPubliclyShared || false);
	const [originalIsPubliclyShared] = React.useState<boolean>(playlist?.isPubliclyShared || false);
	const [isValid, setIsValid] = React.useState(true);
	const [validationMessage, setValidationMessage] = React.useState('');
	const [saving, setSaving] = React.useState(false);

	React.useEffect(() => {
		setPhoneNumbers(playlist?.sharedWith || []);
		setIsPubliclyShared(playlist?.isPubliclyShared || false);
	}, [playlist]);

	const validatePhoneNumber = (phone: string): { isValid: boolean; message: string; formattedPhone?: string } => {
		const trimmedPhone = phone.trim().replace(/^\+91/, '');

		if (!trimmedPhone) {
			return { isValid: false, message: 'Phone number cannot be empty' };
		}

		// Check if phone number starts with +
		if (trimmedPhone.startsWith('+')) {
			// International format validation
			const internationalRegex = /^[+][1-9]\d{1,14}$/;
			if (!internationalRegex.test(trimmedPhone)) {
				return { isValid: false, message: 'Invalid international phone number format' };
			}
			return { isValid: true, message: '', formattedPhone: trimmedPhone };
		} else {
			// Domestic format - should be exactly 10 digits
			const domesticRegex = /^\d{10}$/;
			if (!domesticRegex.test(trimmedPhone)) {
				return { isValid: false, message: 'Phone number should be 10 digits long' };
			}
			// Add +91 prefix for Indian numbers
			return { isValid: true, message: '', formattedPhone: '+91' + trimmedPhone };
		}
	};

	// Check if there are any changes from original
	const hasChanges = () => {
		if (isPubliclyShared !== originalIsPubliclyShared) {
			return true;
		}
		if (phoneNumbers.length !== originalPhoneNumbers.length) {
			return true;
		}
		const sortedCurrent = [...phoneNumbers].sort();
		const sortedOriginal = [...originalPhoneNumbers].sort();
		return !sortedCurrent.every((phone, index) => phone === sortedOriginal[index]);
	};

	const addPhoneNumber = () => {
		const validation = validatePhoneNumber(phoneNumber);

		if (!validation.isValid) {
			setIsValid(false);
			setValidationMessage(validation.message);
			return;
		}

		const formattedPhone = validation.formattedPhone!;

		if (phoneNumbers.includes(formattedPhone)) {
			setIsValid(false);
			setValidationMessage('This phone number has already been added');
			return;
		}

		setPhoneNumbers([...phoneNumbers, formattedPhone]);
		setPhoneNumber('');
		setIsValid(true);
		setValidationMessage('');
	};

	const removePhoneNumber = (phoneToRemove: string) => {
		setPhoneNumbers(phoneNumbers.filter((phone) => phone !== phoneToRemove));
	};

	const handleSave = async () => {
		setSaving(true);
		try {
			await onShare(playlist.id, phoneNumbers, isPubliclyShared);
			onClose();
		} catch (error) {
			console.error('Error sharing playlist:', error);
		} finally {
			if (isPubliclyShared) {
				App.shareLink('', SHARE_ORIGIN + '/playlist/shared-' + playlist.userId + '-' + playlist.id, 'Playlist', playlist.id);
			}
			setSaving(false);
		}
	};

	const handleClose = () => {
		setPhoneNumber('');
		setIsValid(true);
		setSaving(false);
		onClose();
	};

	const handleKeyPress = (event: React.KeyboardEvent) => {
		if (event.key === 'Enter') {
			addPhoneNumber();
		}
	};

	return (
		<Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
			<DialogTitle>Share Playlist: {playlist?.title}</DialogTitle>
			<DialogContent dividers>
				<Box mb={2} sx={{ display: 'flex', flexDirection: 'column' }}>
					<FormControlLabel
						sx={{ mb: 2 }}
						control={<Checkbox checked={isPubliclyShared} onChange={(event) => setIsPubliclyShared(event.target.checked)} color="primary" />}
						label="Share with all public"
					/>
					{!isPubliclyShared && (
						<>
							<Divider />
							<Box
								sx={{
									textAlign: 'center' as 'center',
									backgroundColor: 'white',
									width: '10%',
									marginTop: -2,
									alignSelf: 'center',
									fontSize: '2rem',
								}}
							>
								or
							</Box>
						</>
					)}
				</Box>

				{!isPubliclyShared && (
					<>
						{phoneNumbers.length < 10 ? (
							<Box mb={2}>
								<Typography variant="body2" color="textSecondary" gutterBottom>
									Add phone numbers to share this playlist with:
								</Typography>
								<FormControl fullWidth error={!isValid}>
									<Box display="flex" alignItems="center" gap={1}>
										<Input
											value={phoneNumber}
											placeholder="Enter phone number"
											fullWidth
											onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
												setPhoneNumber(event.target.value);
												setIsValid(true);
												setValidationMessage('');
											}}
											onKeyPress={handleKeyPress}
										/>
										<Button variant="contained" size="small" onClick={addPhoneNumber} disabled={!phoneNumber.trim()}>
											Add
										</Button>
									</Box>
									{!isValid && <FormHelperText>{validationMessage}</FormHelperText>}
								</FormControl>
							</Box>
						) : (
							<Box mt={2}>
								<Typography variant="body2" color="textSecondary" align="center">
									You can share with maximum 10 phone numbers only.
								</Typography>
							</Box>
						)}

						{phoneNumbers.length > 0 && (
							<>
								<Divider />
								<Box mt={2}>
									<Typography variant="subtitle2" gutterBottom>
										Shared with ({phoneNumbers.length}):
									</Typography>
									<List dense>
										{phoneNumbers.map((phone, index) => (
											<ListItem key={index}>
												<ListItemText primary={phone} />
												<ListItemSecondaryAction>
													<IconButton edge="end" aria-label="remove" onClick={() => removePhoneNumber(phone)} size="small">
														{Icons.Delete}
													</IconButton>
												</ListItemSecondaryAction>
											</ListItem>
										))}
									</List>
								</Box>
							</>
						)}

						{phoneNumbers.length === 0 && (
							<Box mt={2}>
								<Typography variant="body2" color="textSecondary" align="center">
									This playlist is not shared with anyone yet.
								</Typography>
							</Box>
						)}
					</>
				)}

				{isPubliclyShared && (
					<Box mt={2}>
						<Typography variant="body2" color="textSecondary" align="center">
							This playlist will be publicly accessible to everyone.
						</Typography>
					</Box>
				)}
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose} disabled={saving}>
					Cancel
				</Button>
				<Button onClick={handleSave} color="primary" disabled={saving || (!hasChanges() && !isPubliclyShared)} variant="contained">
					{saving ? 'Saving...' : isPubliclyShared ? 'Share Link' : 'Save'}
				</Button>
			</DialogActions>
		</Dialog>
	);
}
